import { useEffect, useState, useMemo } from 'react';
import api from '../services/api';

export default function Frequencia({ grupo, usuarioPainel, token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [periodo, setPeriodo] = useState(null);
  const [q, setQ] = useState('');
  const [refreshIndex, setRefreshIndex] = useState(0);

  function formatMesReferencia(mesRef) {
    if (!mesRef) return null;
    // aceitável: 'MM/YYYY' ou 'YYYY-MM' ou 'MM-YYYY' etc.
    if (/^\d{2}\/\d{4}$/.test(mesRef)) {
      const [mm, yyyy] = mesRef.split('/');
      return `${yyyy}-${mm.padStart(2, '0')}`;
    }
    if (/^\d{4}-\d{2}$/.test(mesRef)) return mesRef;
    // fallback: normalize separators and take first 7 chars
    return mesRef.replace(/\//g, '-').slice(0, 7);
  }

  async function handleAtualizar() {
    setLoading(true);
    setError(null);
    try {
      const mesReferencia = formatMesReferencia(periodo?.mes_referencia);
      const payload = {
        mesReferencia,
        grupo_id: grupo?.grupo_id,
        comunidade_id: grupo?.comunidade_id,
        paroquia_id: grupo?.paroquia_id,
      };
      console.log('POST /frequencias/atualizar payload:', payload);
      const res = await api.post('/frequencias/atualizar', payload, { headers: { Authorization: `Bearer ${token}` } });
      console.log('POST /frequencias/atualizar response:', res?.data);
      // após sucesso, refetch
      setRefreshIndex(r => r + 1);
    } catch (err) {
      console.error('Erro ao atualizar frequências:', err);
      setError(err.response?.data?.message || 'Erro ao atualizar frequências.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const ac = new AbortController();
    async function fetchFrequencia() {
      setLoading(true);
      setError(null);
      try {
        const params = [];
        if (grupo?.grupo_id) params.push(`grupo_id=${grupo.grupo_id}`);
        if (grupo?.paroquia_id) params.push(`paroquia_id=${grupo.paroquia_id}`);
        if (grupo?.comunidade_id) params.push(`comunidade_id=${grupo.comunidade_id}`);
        const query = params.length ? `?${params.join('&')}` : '';
        const res = await api.get(`/frequencias${query}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        });
        let payload = res.data;
        // payload may be { success: true, periodo: {...}, data: [...] }
        if (payload && payload.periodo) setPeriodo(payload.periodo);
        let data = payload;
        if (payload && payload.success && Array.isArray(payload.data)) data = payload.data;
        if (!Array.isArray(data)) {
          setItems([]);
          setError('Resposta inesperada do servidor ao buscar frequências.');
        } else {
          data.sort((a,b) => (b.frequencia||0) - (a.frequencia||0));
          setItems(data);
        }
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        if (err.response?.status === 404) setError('Endpoint de frequência não encontrado.');
        else setError('Erro ao buscar frequências.');
      } finally {
        setLoading(false);
      }
    }
    fetchFrequencia();
    return () => ac.abort();
  }, [grupo?.grupo_id, grupo?.paroquia_id, grupo?.comunidade_id, token, refreshIndex]);

  const maxFreq = useMemo(() => items.reduce((m, it) => Math.max(m, Number(it.frequencia||0)), 0), [items]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(i => (i.usuario_nome || '').toLowerCase().includes(term));
  }, [items, q]);

  function exportCsv() {
    const rows = [['usuario_id','usuario_nome','frequencia']];
    filtered.forEach(r => rows.push([r.usuario_id, r.usuario_nome, r.frequencia]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const mes = periodo?.mes_referencia ? periodo.mes_referencia.replace(/\//g,'-') : (grupo?.grupo_id || 'grupo');
    a.download = `frequencias_${grupo?.grupo_id || 'grupo'}_${mes}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="font-sans">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Frequência</h1>
          <p className="text-sm text-slate-600">Quantidade de dias servidos no mês por acolito.</p>
          {periodo && (
            <div className="mt-2 text-sm text-slate-600">
              <div><strong>Período:</strong> {periodo.mes_referencia || ''}</div>
              <div>
                <strong>Escala:</strong>{' '}
                {periodo.data_inicio_escala ? new Date(periodo.data_inicio_escala).toLocaleDateString() : ''}
                {' '}até{' '}
                {periodo.data_fim_escala ? new Date(periodo.data_fim_escala).toLocaleDateString() : ''}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Pesquisar por nome..." className="p-2 border border-slate-300 rounded-md" />
          <button onClick={handleAtualizar} disabled={loading} title="Atualizar frequências" className="px-3 py-2 bg-slate-200 text-slate-800 rounded-md">{loading ? 'Atualizando...' : 'Atualizar'}</button>
          <button onClick={exportCsv} className="px-3 py-2 bg-indigo-600 text-white rounded-md">Exportar CSV</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-lg">{error}</div>}

      {loading ? (
        <p className="text-center text-slate-500">A carregar dados...</p>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">Usuário</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-600">Frequência</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">Visual</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((u, idx) => (
                  <tr key={u.usuario_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-700">{idx+1}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{u.usuario_nome}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{u.frequencia ?? 0}</td>
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-100 rounded overflow-hidden">
                        <div className="h-3 bg-indigo-600" style={{ width: `${maxFreq? (Number(u.frequencia||0)/maxFreq*100) : 0}%` }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-500">Nenhum registro encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
